import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { bilingualLines, loadSearchNormalizer, normalizeForSearch } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import { adminPicksApi, type FeaturablePick } from '../../services/adminPicksApi';

/** Same as featured activities: the homepage row shows eight. */
const MAX_FEATURED = 8;

/**
 * Dashboard "Featured YOYO Picks" (PRD §5.2), laid out like Featured Activities:
 * published Picks on the left, the featured ones in homepage order on the right.
 * Saving makes this list the only source of the Picks row order.
 */
export function FeaturedPicksManager() {
  const [all, setAll] = useState<FeaturablePick[]>([]);
  const [featured, setFeatured] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const siteLanguage = useSiteLanguage();
  // Title search ignores Simplified/Traditional differences (PRD §5.5).
  const [normalizerReady, setNormalizerReady] = useState(false);
  useEffect(() => {
    void loadSearchNormalizer().then(() => setNormalizerReady(true));
  }, []);

  useEffect(() => {
    adminPicksApi
      .featured()
      .then((picks) => {
        setAll(picks);
        setFeatured(
          picks
            .filter((p) => p.isFeatured)
            // Same as the homepage: a featured Pick without an order comes last.
            .sort(
              (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
            )
            .map((p) => p.id)
        );
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load Picks'))
      .finally(() => setLoading(false));
  }, []);

  const available = useMemo(() => {
    const query = searchQuery ? normalizeForSearch(searchQuery) : '';
    return all
      .filter((p) => !featured.includes(p.id))
      .filter(
        (p) =>
          !query ||
          [p.titleCN, p.titleEN, p.providerName].some(
            (text) => !!text && normalizeForSearch(text).includes(query)
          )
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, featured, searchQuery, normalizerReady]);

  const featuredPicks = useMemo(
    () =>
      featured
        .map((id) => all.find((p) => p.id === id))
        .filter((p): p is FeaturablePick => p !== undefined),
    [all, featured]
  );

  const change = (next: string[]) => {
    setFeatured(next);
    setHasChanges(true);
    setError(null);
  };

  const add = (id: string) => {
    if (featured.length >= MAX_FEATURED) {
      setError(`Maximum ${MAX_FEATURED} featured Picks allowed`);
      return;
    }
    change([...featured, id]);
  };

  const move = (index: number, by: -1 | 1) => {
    const next = [...featured];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    change(next);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await adminPicksApi.setFeatured(featured);
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const iconButton =
    'p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed';

  return (
    <div>
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Published Picks</h3>
          <div className="mb-3">
            <input
              type="text"
              placeholder="Search Picks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
            {available.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {searchQuery ? 'No matching Picks' : 'No published Picks'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {available.map((p) => (
                  <li key={p.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                    <PickTitle pick={p} lang={siteLanguage} />
                    <button
                      onClick={() => add(p.id)}
                      disabled={featured.length >= MAX_FEATURED}
                      className="ml-2 px-3 py-1 text-xs font-medium text-primary bg-primary/10 rounded hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      Add
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Featured (homepage order)</h3>
          <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
            {featuredPicks.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No featured Picks selected
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {featuredPicks.map((p, index) => (
                  <li key={p.id} className="flex items-center justify-between p-3 bg-white">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm font-medium text-gray-400 w-5">{index + 1}.</span>
                      <PickTitle pick={p} lang={siteLanguage} />
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => move(index, -1)}
                        disabled={index === 0}
                        className={iconButton}
                        title="Move up"
                        aria-label="Move up"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => move(index, 1)}
                        disabled={index === featuredPicks.length - 1}
                        className={iconButton}
                        title="Move down"
                        aria-label="Move down"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => change(featured.filter((id) => id !== p.id))}
                        className="p-1 text-red-400 hover:text-red-600"
                        title="Remove"
                        aria-label="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          onClick={() => void save()}
          disabled={!hasChanges || saving}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

/** Two-line title, the user's language on top (PRD §5.2); the provider when untitled. */
function PickTitle({ pick, lang }: { pick: FeaturablePick; lang: string }) {
  const [top, bottom] = bilingualLines(pick.titleCN, pick.titleEN, lang);
  return (
    <div className="min-w-0">
      <span className="text-sm text-gray-900 truncate block">{top || pick.providerName}</span>
      {bottom && <span className="text-xs text-gray-500 truncate block">{bottom}</span>}
      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
        {pick.type === 'EVENT' ? 'Event' : 'Provider'}
      </span>
    </div>
  );
}
